import React, { useState, useRef, useEffect } from "react";
import { IconCheckCircle, InfoMark, EditIcon, AccountIcon, CallIcon, NewMailIcon, VerifiedIcon, IconLocation, LocationIcon } from "../../../../assests/icons/Icons";
import ReactTooltip from "../../../../Components/ReactTooltip/ReactTooltip";
import { useAppDispatch, useAppSelector } from "../../../../ReduxTool/store/hooks";
import { setModalHeaderFooter } from "../../../../ReduxTool/Slice/CommonReducers/CommonReducers";
import { getCurrencyLabel, formatCompactMoney, formatCompactNumber, formatMoney, projectCostBreakdown } from "../../../../Utils/commonFunctions";
import { GSTPERCENTAGE, PVNXT_MODE, getInstallationModeContent } from "../../../../Utils/Const";

/* ─────────────────────────────────────────────────────────────
   Payment — Step 3 of the Proposal flow
   
   Modal title  : "Secure your solar installation"
   Primary btn  : "Pay & Start Installation"
   Secondary btn: "Talk to Support"
   ───────────────────────────────────────────────────────────── */

interface PaymentProps {
    /** Passed down from DefaultHeader — the same profileState used in Support */
    profileState: {
        fname: string;
        lname: string;
        mobile: string;
        email: string;
    };
    /** Open the View-Profile modal so the user can edit contact details */
    handleProfileEdit: () => void;
    /** Site address — auto-filled from the map selection */
    siteAddress?: {
        line1: string;
        line2: string;
    };
}

/* ─── Summary metrics ─────────────────────────────────────── */
const BOOKING_BULLETS = [
    "100% secure payment processing",
    "Booking amount is fully refundable if installation cannot proceed.",
    "Remaining amount will be paid in simple milestones during installation.",
];

const WHY_BOOKING = [
    "Start detailed system design and approvals",
    "Reserve your installation slot",
    "Assign a verified installer for your project",
];

const AFTER_PAYMENT = [
    "Final design & documents are prepared",
    "Installer visit and installation schedule is confirmed",
    "You receive clear milestone updates until system is running",
];



/* ─── Cost Breakdown Tooltip Popup ──────────────────────────── */
export const CostBreakdownTooltip = ({ visible, solarSummary = false, type = "projectsummary" }: { visible: boolean, solarSummary?: boolean, type?: "payment" | "projectsummary" }) => {
    const { user: { country_mstr } } = useAppSelector(state => state.auth);
    const { roofAnalysis: { selected_project } } = useAppSelector((state) => state.consumerReducers);
    if (!visible) return null;
    /* ─── Cost breakdown rows (shown in tooltip popup) ─────────── */
    const COST_ROWS = [
        { label: "Solar Installtion", sub: "Calculated after you provide your billing address", value: "INR X,XX,XXX" },
        //   { label: "GST (9%)", sub: "Calculated after you provide your billing address", value: "INR --" },
        //   { label: "Subsidy", sub: "Calculated after you provide your billing address", value: "INR --" },
        //   { label: "Discount", sub: "Calculated after you provide your billing address", value: "INR --" },
    ];
    return (
        <div className={` absolute z-500000 -mt-6 w-[420px] bg-white rounded-xl shadow-2xl overflow-hidden ${type === "projectsummary" ? " left-5 bottom-full " : " left-5 top-full "}`} style={{ minWidth: 360 }}   >
            {/* Header label */}
            <div className="px-5 pt-4 pb-2">
                <p className="text-[10px] font-normal font-Roboto uppercase text-gray-400 px-1">
                    Cost Breakdown
                </p>
            </div>

            <div className="px-5 pb-3 flex flex-col ">
                {COST_ROWS.map((row, index) => (
                    <div key={row.label} className="py-3">
                        <div className="flex justify-between items-start">
                            <span className="heading-16-inter-400">{row.label}</span>
                            {/* <span className="heading-14-inter-600 ml-4 whitespace-nowrap">{index ===0 ? `${getCurrencyLabel(country_mstr)} ${formatCompactNumber((((+selected_project?.projectcost! - (+selected_project?.projectcost! || 0) * (GSTPERCENTAGE / (100 + GSTPERCENTAGE)))||0)  / country_mstr?.currencyrate!),country_mstr?.alpha2code)}` */}
                            <span className="heading-14-inter-600 ml-4 whitespace-nowrap">{index === 0 ? `${getCurrencyLabel(country_mstr)} ${formatCompactNumber(((+selected_project?.projectcost! || 0) / country_mstr?.currencyrate!), country_mstr?.alpha2code)}`
                                : index === 1 ? `${getCurrencyLabel(country_mstr)} ${(formatCompactNumber(((+selected_project?.projectcost! || 0) * (GSTPERCENTAGE / (100 + GSTPERCENTAGE)) / country_mstr?.currencyrate!), country_mstr?.alpha2code))}` : row.value}</span>
                        </div>
                        {!solarSummary && <p className="heading-12-inter-normal-sub mt-2 leading-snug">{row.sub}</p>}
                    </div>
                ))}
            </div>

            {/* Divider */}
            <div className="mx-5 border-t border-gray-200" />

            {/* Total row */}
            <div className="px-5 py-4 flex justify-between items-center">
                <div>
                    <p className=".heading-20-inter-info mb-2">Total system cost</p>
                    <p className="heading-12-inter-normal-sub mt-0.5">Inclusive of all taxes and installation</p>
                </div>
                <span className="heading-20-inter-600 ml-4 whitespace-nowrap">{`${getCurrencyLabel(country_mstr)} ${formatCompactNumber((((+selected_project?.projectcost!) || 0) / country_mstr?.currencyrate!), country_mstr?.alpha2code)}`}</span>
            </div>
        </div>
    );
};

/* ─── Total Cost Card with hover breakdown popup ─────────────── */
const TotalCostCard = () => {
    const { user: { country_mstr } } = useAppSelector(state => state.auth)
    const { roofAnalysis: { selected_project } } = useAppSelector((state) => state.consumerReducers);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleMouseEnter = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setShowBreakdown(true);
    };

    const handleMouseLeave = () => {
        timerRef.current = setTimeout(() => setShowBreakdown(false), 150);
    };

    // Close on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowBreakdown(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <div className="bg-white px-5 py-4 -mt-4" ref={wrapperRef}>
            <div className="flex justify-between items-start mb-1">
                <span className="text-[16px] font-semibold text-gray-900 flex items-center gap-1 mb-2">
                    Total system cost
                    {/* ── Info icon — hover triggers breakdown popup ── */}
                    {/* ── Cost breakdown tooltip ── */}
                    <span className="cursor-pointer relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}  >
                        <InfoMark />
                        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}  >
                            <CostBreakdownTooltip visible={showBreakdown} type={"payment"} solarSummary />
                        </div>
                    </span>
                </span>
                <span className="text-[16px] font-semibold text-gray-900">{`${getCurrencyLabel(country_mstr)} ${formatCompactNumber((((+selected_project?.projectcost!) || 0) / country_mstr?.currencyrate!), country_mstr?.alpha2code)}`}</span>
            </div>
            <p className="text-[11px] text-black text-opacity-70">
                Inclusive of all taxes and installation. No hidden charges, all costs already included
            </p>
        </div>
    );
};

/* ─── Site Address card ───────────────────────────────────── */
interface SiteAddressCardProps {
    siteAddress?: { line1: string; line2: string };
}

const SiteAddressCard = () => {
    const dispatch = useAppDispatch();
    const { roofAnalysis: { formDetails: { projectSetup } }, } = useAppSelector((state) => state.consumerReducers);


    const handleSiteAddress = () => {
        dispatch(setModalHeaderFooter({ title: "", btnTxt: "", secondaryBtnTxt: "", modalid: "" }));
        dispatch(setModalHeaderFooter({ title: "Site Address", btnTxt: "Save Address", secondaryBtnTxt: "Back", modalid: "siteaddress" }));
    }

    return (
        <div>
            {/* <BoxUiUserDetails /> */}
            {/* <BoxUiUserDetails /> */}
            <div className="flex flex-col gap-2 w-full rounded-xl border border-[#D9D9D9] bg-[rgba(244,244,245,0.6)] p-3">
                {/* Header */}
                <div className="flex justify-between items-center">
                    {/* <p className="text-12-roboto leading-none flex items-center gap-1 uppercase"> SITE ADDRESS <span data-tooltip-id="useraddresstooltip"> <InfoMark />   </span> </p> */}
                    <p className="text-12-roboto leading-none flex items-center gap-1 uppercase"> SITE ADDRESS  </p>
                    {/* <button type="button" onClick={handleSiteAddress} className=" size-4 flex items-center justify-center" > <EditIcon /> </button> */}
                </div>

                {/* SUB HEADING */}
                <div className='w-full text-10-roboto flex items-center gap-1'>
                    <p>Auto-filled from your selected map location. You can edit if needed.</p>
                </div>

                {/* Content */}
                <div className="flex w-full text-sm text-gray-700 gap-3">
                    <div className="mt-[2px]" >  <LocationIcon /> </div>
                    <div className="pr-2 overflow-hidden w-full">
                        <h4 className='turncate heading-14-roboto-normal overflow-hidden text-ellipsis'>{projectSetup?.addressline1}</h4>
                        <div className='w-full flex items-center gap-1'>
                            <p className="text-12-roboto leading-normal">{projectSetup?.city}, {projectSetup?.state}, {projectSetup?.country}, {projectSetup?.pincode} </p>
                        </div>
                    </div>

                </div>

            </div>
            <ReactTooltip Side="LSB" place="right" id="siteAddressTooltip" content="Edit user details." />
        </div>
    )
}

/* ─── Main Payment component ──────────────────────────────── */

const Payment = () => {
    const { user: { country_mstr } } = useAppSelector(state => state.auth)
    const { roofAnalysis: { selected_project } } = useAppSelector(state => state.consumerReducers);

    const modeContent = getInstallationModeContent(selected_project?.installationmode);


    const bookingCost = `${getCurrencyLabel(country_mstr)} ${formatCompactNumber((projectCostBreakdown(selected_project).booking / country_mstr?.currencyrate!), country_mstr?.alpha2code)}`
    return (
        <div className="font-sans overflow-hidden min-w-[800px] border border-gray-200 rounded-md">
            {/* ── Body ── */}
            <div className="p-4 flex flex-col gap-3 bg-white overflow-y-auto custom-scrollbar-css max-h-[55vh]">
                {/* Summary section label */}
                <p className="text-[10px] font-Roboto uppercase text-gray-400 px-1">
                    Summary
                </p>

                {/* Two-column layout */}
                <div className="grid grid-cols-2 gap-3">

                    {/* ── Left column ── */}
                    <div className="flex flex-col p-4 rounded-lg">
                        {/* Total cost card with hover breakdown popup */}
                        <TotalCostCard />

                        {/* Booking amount */}
                        <div className="rounded-xl border border-gray-200 px-5 py-4 flex flex-col gap-3 bg-[#D9D9D929]">
                            <div className="flex justify-between items-start">
                                <span className="text-[16px] font-semibold text-gray-900 flex items-center gap-1">
                                    Booking amount
                                    {/* <span data-tooltip-id="bookingTooltip" className="cursor-pointer">
                                        <InfoMark />
                                    </span> */}
                                </span>

                                <span className="text-[16px] font-semibold text-gray-900">{bookingCost}</span>
                            </div>

                            {/* Bullet points */}
                            <ul className="flex flex-col gap-4">
                                {BOOKING_BULLETS.map((b) => (
                                    <li key={b} className="flex items-start gap-2">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-800 flex-shrink-0" />
                                        <span className="text-[12px] text-gray-700 font-inter">{b}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* Why this payment */}
                            <div>
                                <p className="text-[12px] font-semibold font-inter text-[#000000] text-opacity-70 mb-4">
                                    Why this payment is needed &amp; how your booking amount helps us?
                                </p>
                                <div className="flex flex-col gap-4">
                                    {WHY_BOOKING.map((item) => (
                                        <div key={item} className="flex items-center gap-2">
                                            <IconCheckCircle />
                                            <span className="text-[12px] text-gray-700 font-inter">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Right column ── */}
                    <div className="flex flex-col gap-3 border-l border-gray-200 pl-5">
                        {/* Site address */}
                        <SiteAddressCard />

                        {/* Selected installation mode badge */}
                        <div className="rounded-xl border border-[#D9D9D9] bg-white px-5 py-4">
                            <div className="flex justify-between items-start mb-1">
                                <p className="text-[17px] font-inter font-medium text-[#2E2222]">{modeContent.title}</p>
                                <span className="text-[11px] font-medium text-[#000000] border border-[#616161] rounded-full px-3 py-0.5 whitespace-nowrap">
                                    Mode Selected
                                </span>
                            </div>
                            <p className="text-[12px] text-black font-inter text-opacity-80">{modeContent.tagline}</p>
                        </div>


                        {/* What happens after payment */}
                        <div className="rounded-xl bg-white px-5 py-4">
                            <p className="text-[14px] font-inter text-[#2E2222] mb-3">
                                What happens after payment?
                            </p>
                            <div className="flex flex-col gap-2.5">
                                {AFTER_PAYMENT.map((item) => (
                                    <div key={item} className="flex items-start gap-2">
                                        <IconCheckCircle />
                                        <span className="text-[12px] text-black font-inter">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Payment;